import { ComponentProps, Dispatch, SetStateAction, useMemo } from 'react';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useTranslation } from '@frontend/i18n';
import { LocationPhoneSelector } from '@frontend/location-phone-selector';
import { PersonPhoneSelector, type PersonPhoneSelectorState } from '@frontend/person-phone-selector';
import { AssociatedContactsListItem, ThreadDrawer, ThreadPopoverDrawer } from '@frontend/thread-header';
import { theme } from '@frontend/theme';
import { Button, Text, usePopoverMenu } from '@frontend/design-system';
import { MiniChatHeader } from '../mini-chat-header';
import { ThreadOptionsMenu } from './thread-options-menu';
import { ThreadOptionsSubmenu } from './thread-options-submenu';

export type MenuOption = 'change-thread' | 'associated-contacts' | 'associated-contacts-template';

export type ThreadOptionsDrawerState = {
  selectedMenu: MenuOption | undefined;
  setSelectedMenu: Dispatch<SetStateAction<MenuOption | undefined>>;
  setPrimaryContact: (person: {
    personId: string;
    firstName: string;
    preferredName: string;
    lastName: string;
  }) => Promise<void>;
  setContextContactId: (contactId: string) => void;
  hideOptions: MenuOption[];
  associatedContacts: Person[];
  personPhoneSelectorState: PersonPhoneSelectorState;
  locationPhoneSelectorState: Omit<ComponentProps<typeof LocationPhoneSelector>, 'onSelectLocationPhone'>;
  primaryContactId?: string;
};

type ThreadOptionsDrawerProps = ThreadOptionsDrawerState &
  Pick<ComponentProps<typeof ThreadDrawer>, 'open' | 'onClose' | 'closeButtonRef'> &
  Pick<
    ComponentProps<typeof MiniChatHeader>,
    'onSelectPersonPhone' | 'onSelectLocationPhone' | 'groupId' | 'threadId' | 'locationPhone'
  >;

export const ThreadOptionsDrawer = ({
  selectedMenu,
  setSelectedMenu,
  setPrimaryContact,
  setContextContactId,
  hideOptions,
  associatedContacts,
  personPhoneSelectorState,
  locationPhoneSelectorState,
  onSelectPersonPhone,
  onSelectLocationPhone: _onSelectLocationPhone,
  groupId,
  primaryContactId,
  threadId,
  locationPhone,
  ...rest
}: ThreadOptionsDrawerProps) => {
  const { t } = useTranslation('mini-chat');

  const { primaryContact, otherContacts } = useMemo(
    () =>
      associatedContacts.reduce<{ primaryContact?: Person; otherContacts: Person[] }>(
        (acc, curr) => {
          if (!acc.primaryContact && curr.personId === primaryContactId) {
            acc.primaryContact = curr;
          } else if (curr.personId !== primaryContactId) {
            acc.otherContacts.push(curr);
          }

          return acc;
        },
        { otherContacts: [] }
      ),
    [associatedContacts, primaryContactId]
  );

  // We need to disable close on click outside of the drawer if the location phone selection menu is open, so we set up
  // the popover menu state here.
  const locationPhonePopoverState = usePopoverMenu({ placement: 'bottom-start' });

  const handleSelectLocationPhone: typeof _onSelectLocationPhone = _onSelectLocationPhone
    ? (...args) => {
        _onSelectLocationPhone(...args);
        locationPhonePopoverState.close();
        rest.onClose();
      }
    : undefined;

  if (hideOptions.length > 1) return null;

  const hasMoreThanOnePhoneOption = personPhoneSelectorState.allPhones.filter((phone) => phone.isValid).length > 1;

  return (
    <>
      <ThreadDrawer
        closeLabel={t('Close Thread Details')}
        css={{
          borderTop: `1px solid ${theme.colors.neutral20}`,
          overflowY: 'hidden',
        }}
        {...rest}
        open={locationPhonePopoverState.isOpen || (selectedMenu !== 'associated-contacts' && rest.open)}
        closeOnClickOutside={!locationPhonePopoverState.isOpen}
      >
        {selectedMenu === undefined ? (
          <ThreadOptionsMenu
            key='thread-options-menu'
            threadId={threadId}
            groupId={groupId}
            locationPhone={locationPhone}
            setSelectedMenu={setSelectedMenu}
            hideOptions={hideOptions}
            associatedContactsCount={associatedContacts.length}
          />
        ) : selectedMenu === 'change-thread' ? (
          <ThreadOptionsSubmenu
            key='change-thread-menu'
            backLabel={t('Change Thread')}
            onBack={() => setSelectedMenu(undefined)}
          >
            <PersonPhoneSelector
              {...personPhoneSelectorState}
              onSelectPersonPhone={({ personPhone }) => {
                if (!personPhone) {
                  return;
                }
                rest.onClose();
                onSelectPersonPhone(personPhone);
              }}
              css={{
                justifyContent: 'start',
                // the button already has padding if it has more than one option, so we reduce it here
                padding: hasMoreThanOnePhoneOption ? theme.spacing(0.25, 1) : theme.spacing(1),
                borderBottom: `1px solid ${theme.colors.neutral20}`,
              }}
              locationId={groupId}
              variant='normal'
            />
            {handleSelectLocationPhone && (
              <LocationPhoneSelector
                {...locationPhoneSelectorState}
                onSelectLocationPhone={handleSelectLocationPhone}
                css={{
                  justifyContent: 'start',
                  padding: theme.spacing(0.25, 1),
                  borderBottom: `1px solid ${theme.colors.neutral20}`,
                }}
                variant='normal'
                popoverState={locationPhonePopoverState}
              />
            )}
          </ThreadOptionsSubmenu>
        ) : null}
      </ThreadDrawer>
      <ThreadPopoverDrawer
        open={selectedMenu === 'associated-contacts' || selectedMenu === 'associated-contacts-template'}
        onClose={() => {
          rest.onClose();
          setSelectedMenu(undefined);
        }}
        closeLabel={t('Close')}
        size='small'
      >
        <div
          css={{
            padding: theme.spacing(1),
            borderBottom: `1px solid ${theme.colors.neutral10}`,
            width: '100%',
          }}
        >
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              iconName='back'
              variant='secondary'
              size='small'
              css={{
                width: theme.spacing(3),
                height: theme.spacing(3),
                padding: theme.spacing(0.5),
              }}
              onClick={() => {
                if (selectedMenu === 'associated-contacts-template') {
                  rest.onClose();
                }
                setSelectedMenu(undefined);
              }}
            />
            <Text size='medium' weight='bold'>
              {t('Contacts Under This Number')}
            </Text>
            <Text
              color='light'
              size='small'
              css={{
                width: theme.spacing(2),
                height: theme.spacing(2),
              }}
              data-testid='mini-chat-associated-contacts-count'
            >
              {associatedContacts?.length ?? 1}
            </Text>
          </div>
          <Text
            size='small'
            color='light'
            textAlign='center'
            css={{
              width: '100%',
            }}
          >
            {t('Select to focus another contact...')}
          </Text>
        </div>
        <ul
          css={{
            width: '100%',
            listStyle: 'none',
            margin: 0,
            paddingInlineStart: 0,
            maxHeight: '25vh',
            overflowY: 'auto',
          }}
        >
          {!!primaryContact && (
            <AssociatedContactsListItem
              personId={primaryContact.personId}
              person={primaryContact}
              onSelect={() => {
                setContextContactId(primaryContact.personId);
                rest.onClose();
              }}
              onSetPrimaryContact={() => {
                rest.onClose();
              }}
              isPrimary
              locationId={groupId}
              size='small'
            />
          )}
          {otherContacts.map((person) => (
            <AssociatedContactsListItem
              key={person.personId}
              personId={person.personId}
              person={person}
              onSelect={() => {
                setContextContactId(person.personId);
                rest.onClose();
              }}
              onSetPrimaryContact={() => {
                setPrimaryContact({
                  personId: person.personId,
                  firstName: person.firstName ?? '',
                  preferredName: person.preferredName ?? '',
                  lastName: person.lastName ?? '',
                });
                rest.onClose();
              }}
              isPrimary={false}
              locationId={groupId}
              size='small'
            />
          ))}
        </ul>
      </ThreadPopoverDrawer>
    </>
  );
};

export const buttonStyles = css({
  width: '100%',
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  transition: 'background-color 200ms ease-in-out',
  ':hover, :focus': {
    backgroundColor: theme.colors.neutral5,
    outlineColor: theme.colors.primary50,
  },
  ':active': {
    backgroundColor: theme.colors.neutral10,
  },
});
